import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDays,
  faNetworkWired,
  faLink,
  faCamera,
  faShieldAlt,
  faBullseye,
  faBolt,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ActionDetailModal from '../common/ActionDetailModal';
import AttackChainVisualization from './AttackChainVisualization';

const AttackPathVisualization = ({ actions, targets, projectId, webTargets = [], webFindings = [] }) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [viewMode, setViewMode] = useState('network'); // timeline, network, killchain
  const [dimensions, setDimensions] = useState({ width: 1000, height: 600 });
  const [filterOptions, setFilterOptions] = useState({
    showSuccessfulOnly: false,
    showCriticalOnly: false,
    showTechniquesOnly: false
  });

  useEffect(() => {
    // Update dimensions on resize
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: width - 40, height: 600 });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!actions || actions.length === 0) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    switch (viewMode) {
      case 'timeline':
        // Timeline now rendered by AttackChainVisualization component
        break;
      case 'network':
        drawEnhancedNetworkView();
        break;
      case 'killchain':
        drawKillChainView();
        break;
      default:
        drawEnhancedNetworkView();
    }
  }, [actions, targets, viewMode, dimensions, filterOptions, webTargets, webFindings]);

  const exportToPNG = () => {
    const svg = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      // Download the image
      const a = document.createElement('a');
      a.download = `attack-path-${viewMode}-${Date.now()}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const drawEnhancedNetworkView = () => {
    const svg = d3.select(svgRef.current);
    const { width, height } = dimensions;

    // Clear previous
    svg.selectAll('*').remove();

    // Create hierarchical data structure
    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    // Professional color scheme with blue tones
    const colors = {
      project: '#003d82', // dark blue
      target: {
        ip: '#0066cc',          // blue
        hostname: '#0080ff',    // light blue
        cloudResource: '#4d94ff', // lighter blue
        apiEndpoint: '#66a3ff', // pale blue
        container: '#1a75ff',   // medium blue
        identity: '#0052cc',    // darker blue
        application: '#3385ff', // bright blue
        network: '#005ce6'      // navy blue
      },
      tactic: '#004080',    // dark blue for tactics
      technique: '#0066cc', // medium blue for techniques
      test: {  // tests are actions - keeping severity colors
        successful: '#28a745',
        detected: '#ffc107',
        blocked: '#dc3545',
        failed: '#6c757d',
        partial: '#17a2b8',
        unknown: '#6610f2'
      }
    };

    // Filter actions based on options
    let filteredActions = actions;
    if (filterOptions.showSuccessfulOnly) {
      filteredActions = actions.filter(a => a.result === 'successful');
    }

    // Get project name from the first action or use projectId
    let projectName = 'Project';
    if (filteredActions.length > 0 && filteredActions[0].project) {
      if (typeof filteredActions[0].project === 'object' && filteredActions[0].project.name) {
        projectName = filteredActions[0].project.name;
      }
    }

    // Debug logging
    console.log('Drawing network with:', {
      projectName,
      totalTargets: targets.length,
      totalActions: actions.length,
      filteredActions: filteredActions.length,
      firstAction: filteredActions[0],
      firstTarget: targets[0]
    });

    // 1. Add Project Root Node
    const projectNode = {
      id: 'project-root',
      type: 'project',
      label: projectName,
      radius: 35,
      fx: width * 0.1,  // Fix position on left
      fy: height / 2
    };
    nodes.push(projectNode);
    nodeMap.set(projectNode.id, projectNode);

    // 2. Collect all relevant target IDs from actions
    const relevantTargetIds = new Set();
    filteredActions.forEach(action => {
      if (action.targets && Array.isArray(action.targets)) {
        action.targets.forEach(targetRef => {
          // Handle both string IDs and object references
          const targetId = typeof targetRef === 'string' ? targetRef : targetRef._id;
          if (targetId) {
            relevantTargetIds.add(targetId);
          }
        });
      }
    });

    console.log('Relevant target IDs:', Array.from(relevantTargetIds));

    // 3. Add Target nodes
    let targetCount = 0;
    targets.forEach((target) => {
      // Check if this target is referenced by any action
      const isRelevant = relevantTargetIds.has(target._id) || 
                        relevantTargetIds.has(target.id) ||
                        (relevantTargetIds.size === 0 && !filterOptions.showSuccessfulOnly); // Show all if no filters
      
      if (!isRelevant) {
        console.log('Skipping irrelevant target:', target._id);
        return;
      }
      
      // Apply critical filter if enabled
      if (filterOptions.showCriticalOnly && target.metadata?.criticality !== 'critical') {
        console.log('Skipping non-critical target:', target._id);
        return;
      }
      
      const targetNode = {
        id: `target-${target._id}`,
        type: 'target',
        subType: target.type,
        data: target,
        label: target.value || 'Unknown Target',
        description: target.description,
        criticality: target.metadata?.criticality || 'medium',
        radius: target.metadata?.criticality === 'critical' ? 25 : 
                target.metadata?.criticality === 'high' ? 20 : 15
      };
      nodes.push(targetNode);
      nodeMap.set(targetNode.id, targetNode);
      targetCount++;
      
      // Link project to target
      links.push({
        source: projectNode.id,
        target: targetNode.id,
        type: 'has-target',
        strength: 0.8
      });
    });

    console.log(`Added ${targetCount} targets to the graph`);

    // If no targets were added but we have some in the data, add them all
    if (targetCount === 0 && targets.length > 0) {
      console.log('No targets matched filters, adding all targets');
      targets.forEach((target) => {
        if (filterOptions.showCriticalOnly && target.metadata?.criticality !== 'critical') {
          return;
        }
        
        const targetNode = {
          id: `target-${target._id}`,
          type: 'target',
          subType: target.type,
          data: target,
          label: target.value || 'Unknown Target',
          description: target.description,
          criticality: target.metadata?.criticality || 'medium',
          radius: target.metadata?.criticality === 'critical' ? 25 : 
                  target.metadata?.criticality === 'high' ? 20 : 15
        };
        nodes.push(targetNode);
        nodeMap.set(targetNode.id, targetNode);
        
        // Link project to target
        links.push({
          source: projectNode.id,
          target: targetNode.id,
          type: 'has-target',
          strength: 0.8
        });
      });
    }

    // 4. Add MITRE Tactic nodes
    const tacticSet = new Set();
    const tacticToTargets = new Map(); // Track which tactics relate to which targets
    
    filteredActions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        tacticSet.add(action.mitreAttackTechnique.tactic);
        
        // Map tactics to targets through actions
        if (action.targets && Array.isArray(action.targets)) {
          action.targets.forEach(targetRef => {
            const targetId = typeof targetRef === 'string' ? targetRef : targetRef._id;
            if (targetId) {
              const key = `${action.mitreAttackTechnique.tactic}-${targetId}`;
              if (!tacticToTargets.has(key)) {
                tacticToTargets.set(key, {
                  tactic: action.mitreAttackTechnique.tactic,
                  targetId: targetId
                });
              }
            }
          });
        }
      }
    });

    Array.from(tacticSet).forEach(tactic => {
      const tacticId = `tactic-${tactic.replace(/\s+/g, '-')}`;
      const tacticNode = {
        id: tacticId,
        type: 'tactic',
        label: tactic,
        radius: 20
      };
      nodes.push(tacticNode);
      nodeMap.set(tacticId, tacticNode);
    });

    // Link targets to tactics based on actions
    tacticToTargets.forEach(({tactic, targetId}) => {
      const tacticId = `tactic-${tactic.replace(/\s+/g, '-')}`;
      const targetNodeId = `target-${targetId}`;
      
      if (nodeMap.has(tacticId) && nodeMap.has(targetNodeId)) {
        // Check if link already exists
        const linkExists = links.some(l => 
          l.source === targetNodeId && l.target === tacticId
        );
        
        if (!linkExists) {
          links.push({
            source: targetNodeId,
            target: tacticId,
            type: 'enables-tactic',
            strength: 0.6
          });
        }
      }
    });

    // 5. Add MITRE Technique nodes
    const techniqueMap = new Map();
    filteredActions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const tech = action.mitreAttackTechnique;
        const techniqueId = `technique-${tech.id}`;
        
        if (!techniqueMap.has(techniqueId)) {
          const techniqueNode = {
            id: techniqueId,
            type: 'technique',
            data: tech,
            label: tech.id,
            name: tech.name,
            tactic: tech.tactic,
            radius: 12
          };
          nodes.push(techniqueNode);
          nodeMap.set(techniqueId, techniqueNode);
          techniqueMap.set(techniqueId, techniqueNode);
          
          // Link tactic to technique
          if (tech.tactic) {
            const tacticId = `tactic-${tech.tactic.replace(/\s+/g, '-')}`;
            if (nodeMap.has(tacticId)) {
              links.push({
                source: tacticId,
                target: techniqueId,
                type: 'contains-technique',
                strength: 0.7
              });
            }
          }
        }
      }
    });

    // 6. Add Test nodes (Actions)
    filteredActions.forEach((action) => {
      const testNode = {
        id: `test-${action._id}`,
        type: 'test',
        subType: action.result || 'unknown',
        data: action,
        label: action.title || 'Unnamed Test',
        timestamp: action.startTimestamp || action.timestamp,
        technique: action.mitreAttackTechnique,
        radius: action.result === 'successful' ? 18 : 15
      };
      nodes.push(testNode);
      nodeMap.set(testNode.id, testNode);

      // Link technique to test
      if (action.mitreAttackTechnique) {
        links.push({
          source: `technique-${action.mitreAttackTechnique.id}`,
          target: testNode.id,
          type: 'implemented-by',
          strength: 0.6
        });
      }
      
      // Create crosslinks: tests can also directly link to targets they affected
      if (action.targets && Array.isArray(action.targets)) {
        action.targets.forEach(targetRef => {
          const targetId = typeof targetRef === 'string' ? targetRef : targetRef._id;
          const targetNodeId = `target-${targetId}`;
          if (nodeMap.has(targetNodeId)) {
            links.push({
              source: testNode.id,
              target: targetNodeId,
              type: 'tested-on',
              result: action.result,
              strength: 0.3
            });
          }
        });
      }
    });

    // 7. Add Web Target and Web Finding nodes (only when data exists)
    if (webTargets.length > 0) {
      webTargets.forEach(wt => {
        const wtNode = {
          id: `webtarget-${wt._id}`,
          type: 'webtarget',
          data: wt,
          label: wt.baseUrl || 'Web Target',
          radius: 18
        };
        nodes.push(wtNode);
        nodeMap.set(wtNode.id, wtNode);

        // Link project to web target
        links.push({
          source: projectNode.id,
          target: wtNode.id,
          type: 'has-webtarget',
          strength: 0.6
        });
      });

      webFindings.forEach(wf => {
        const wfNode = {
          id: `webfinding-${wf._id}`,
          type: 'webfinding',
          data: wf,
          label: (wf.title || '').substring(0, 20),
          severity: wf.severity,
          radius: 10
        };
        nodes.push(wfNode);
        nodeMap.set(wfNode.id, wfNode);

        // Link web target to web finding
        const wtId = `webtarget-${wf.webTarget}`;
        if (nodeMap.has(wtId)) {
          links.push({
            source: wtId,
            target: wfNode.id,
            type: 'has-webfinding',
            strength: 0.5
          });
        }

        // Cross-link to MITRE technique if mapping exists
        if (wf.mitreTechnique?.id) {
          const techId = `technique-${wf.mitreTechnique.id}`;
          if (nodeMap.has(techId)) {
            links.push({
              source: wfNode.id,
              target: techId,
              type: 'maps-to-technique',
              strength: 0.4
            });
          }
        }
      });
    }

    // Create sequential links between tests (temporal flow)
    const sortedTests = filteredActions
      .sort((a, b) => new Date(a.startTimestamp || a.timestamp) - new Date(b.startTimestamp || b.timestamp));
    
    for (let i = 0; i < sortedTests.length - 1; i++) {
      links.push({
        source: `test-${sortedTests[i]._id}`,
        target: `test-${sortedTests[i + 1]._id}`,
        type: 'sequence',
        strength: 0.2
      });
    }

    // Apply filters
    if (filterOptions.showTechniquesOnly) {
      // Keep project, tactics, techniques, and tests with techniques
      const filteredNodeIds = new Set();
      nodes.forEach(n => {
        if (n.type === 'project' || n.type === 'tactic' || n.type === 'technique' || 
            (n.type === 'test' && n.technique)) {
          filteredNodeIds.add(n.id);
        }
      });
      
      const originalNodeCount = nodes.length;
      nodes = nodes.filter(n => filteredNodeIds.has(n.id));
      links = links.filter(l => 
        filteredNodeIds.has(l.source) && filteredNodeIds.has(l.target)
      );
      console.log(`Filtered from ${originalNodeCount} to ${nodes.length} nodes`);
    }

    console.log('Final network view:', {
      nodesCount: nodes.length,
      linksCount: links.length,
      nodeTypes: nodes.reduce((acc, n) => {
        acc[n.type] = (acc[n.type] || 0) + 1;
        return acc;
      }, {})
    });

    // If no nodes after filtering, show message
    if (nodes.length === 0) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('fill', '#6b7280')
        .text('No data to display with current filters');
      return;
    }

    // Add background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', darkMode ? '#1f2937' : 'white');

    // Create container for zoom
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Add arrow markers
    svg.append('defs').selectAll('marker')
      .data(['has-target', 'enables-tactic', 'contains-technique', 'implemented-by', 'tested-on', 'sequence'])
      .enter().append('marker')
      .attr('id', d => `arrow-${d}`)
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', d => {
        if (d === 'has-target') return '#003d82';
        if (d === 'enables-tactic') return '#0066cc';
        if (d === 'contains-technique') return '#004080';
        if (d === 'implemented-by') return '#0066cc';
        if (d === 'tested-on') return '#28a745';
        if (d === 'sequence') return '#6c757d';
        return '#0066cc';
      });

    // Create force simulation with hierarchical layout
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .distance(d => {
          if (d.type === 'has-target') return 150;
          if (d.type === 'enables-tactic') return 120;
          if (d.type === 'contains-technique') return 100;
          if (d.type === 'implemented-by') return 80;
          if (d.type === 'tested-on') return 100;
          if (d.type === 'sequence') return 60;
          return 100;
        })
        .strength(d => d.strength || 0.5))
      .force('charge', d3.forceManyBody()
        .strength(d => {
          if (d.type === 'project') return -1000;
          if (d.type === 'target') return -600;
          if (d.type === 'webtarget') return -500;
          if (d.type === 'tactic') return -500;
          if (d.type === 'technique') return -300;
          if (d.type === 'test') return -200;
          if (d.type === 'webfinding') return -150;
          return -150;
        }))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide()
        .radius(d => d.radius + 15))
      .force('x', d3.forceX()
        .x(d => {
          // Create horizontal layers for hierarchy
          if (d.type === 'project') return width * 0.1;
          if (d.type === 'target') return width * 0.3;
          if (d.type === 'webtarget') return width * 0.3;
          if (d.type === 'tactic') return width * 0.5;
          if (d.type === 'technique') return width * 0.7;
          if (d.type === 'test') return width * 0.9;
          if (d.type === 'webfinding') return width * 0.85;
          return width / 2;
        })
        .strength(0.4));

    // Draw links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter().append('line')
      .attr('stroke', d => {
        if (d.type === 'has-target') return '#003d82';
        if (d.type === 'enables-tactic') return '#0066cc';
        if (d.type === 'contains-technique') return '#004080';
        if (d.type === 'implemented-by') return '#0066cc';
        if (d.type === 'tested-on') return colors.test[d.result] || '#28a745';
        if (d.type === 'sequence') return '#6c757d';
        return '#6c757d';
      })
      .attr('stroke-width', d => {
        if (d.type === 'has-target') return 3;
        if (d.type === 'enables-tactic') return 2;
        if (d.type === 'contains-technique') return 2;
        if (d.type === 'implemented-by') return 2;
        if (d.type === 'tested-on') return 1.5;
        if (d.type === 'sequence') return 1;
        return 1;
      })
      .attr('stroke-dasharray', d => {
        if (d.type === 'sequence') return '5,5';
        if (d.type === 'tested-on') return '3,3';
        return 'none';
      })
      .attr('opacity', d => {
        if (d.type === 'sequence') return 0.3;
        if (d.type === 'tested-on') return 0.5;
        return 0.7;
      })
      .attr('marker-end', d => `url(#arrow-${d.type})`)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        // Highlight this link
        d3.select(this)
          .attr('opacity', 1)
          .attr('stroke-width', () => {
            if (d.type === 'has-target') return 5;
            if (d.type === 'enables-tactic') return 4;
            if (d.type === 'contains-technique') return 4;
            if (d.type === 'implemented-by') return 4;
            if (d.type === 'tested-on') return 3;
            return 3;
          })
          .style('filter', 'drop-shadow(0 0 6px rgba(255, 255, 255, 1))');

        // Highlight connected nodes
        const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
        const targetId = typeof d.target === 'object' ? d.target.id : d.target;

        node.selectAll('circle, rect, polygon')
          .style('filter', n => {
            if (n.id === sourceId || n.id === targetId) {
              return 'drop-shadow(0 0 8px rgba(255, 200, 0, 0.9))';
            }
            return 'none';
          })
          .style('opacity', n => {
            if (n.id === sourceId || n.id === targetId) {
              return 1;
            }
            return 0.3;
          });

        // Dim other links
        link.each(function(l) {
          if (l !== d) {
            d3.select(this).attr('opacity', 0.1);
          }
        });
      })
      .on('mouseout', function(event, d) {
        // Reset this link
        d3.select(this)
          .attr('opacity', () => {
            if (d.type === 'sequence') return 0.3;
            if (d.type === 'tested-on') return 0.5;
            return 0.7;
          })
          .attr('stroke-width', () => {
            if (d.type === 'has-target') return 3;
            if (d.type === 'enables-tactic') return 2;
            if (d.type === 'contains-technique') return 2;
            if (d.type === 'implemented-by') return 2;
            if (d.type === 'tested-on') return 1.5;
            if (d.type === 'sequence') return 1;
            return 1;
          })
          .style('filter', 'none');

        // Reset all nodes
        node.selectAll('circle, rect, polygon')
          .style('filter', 'none')
          .style('opacity', 1);

        // Reset all links
        link.attr('opacity', l => {
          if (l.type === 'sequence') return 0.3;
          if (l.type === 'tested-on') return 0.5;
          return 0.7;
        });
      });

    // Draw nodes
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter().append('g')
      .attr('cursor', 'pointer')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Add node shapes
    node.each(function(d) {
      const selection = d3.select(this);
      
      if (d.type === 'project') {
        // Project as a large rounded rectangle
        selection.append('rect')
          .attr('x', -d.radius * 1.5)
          .attr('y', -d.radius)
          .attr('width', d.radius * 3)
          .attr('height', d.radius * 2)
          .attr('rx', 10)
          .style('fill', colors.project)
          .style('stroke', '#fff')
          .style('stroke-width', 3);
      } else if (d.type === 'target') {
        // Targets as hexagons
        const hexagon = (r) => {
          const angles = d3.range(0, 2 * Math.PI, Math.PI / 3);
          return angles.map(a => [r * Math.cos(a), r * Math.sin(a)]).join(' ');
        };
        
        selection.append('polygon')
          .attr('points', hexagon(d.radius))
          .style('fill', colors.target[d.subType] || '#6b7280')
          .style('stroke', '#fff')
          .style('stroke-width', 2)
          .attr('opacity', d.criticality === 'critical' ? 1 : 0.8);
          
        // Add criticality indicator
        if (d.criticality === 'critical') {
          selection.append('circle')
            .attr('r', d.radius + 5)
            .style('fill', 'none')
            .style('stroke', '#dc3545')
            .style('stroke-width', 2)
            .style('stroke-dasharray', '3,3');
        }
      } else if (d.type === 'tactic') {
        // Tactics as rounded rectangles
        selection.append('rect')
          .attr('x', -d.radius * 2)
          .attr('y', -d.radius)
          .attr('width', d.radius * 4)
          .attr('height', d.radius * 2)
          .attr('rx', 10)
          .style('fill', colors.tactic)
          .style('stroke', '#fff')
          .style('stroke-width', 2);
      } else if (d.type === 'technique') {
        // Techniques as diamonds
        selection.append('rect')
          .attr('x', -d.radius)
          .attr('y', -d.radius)
          .attr('width', d.radius * 2)
          .attr('height', d.radius * 2)
          .attr('transform', 'rotate(45)')
          .style('fill', colors.technique)
          .style('stroke', '#fff')
          .style('stroke-width', 2);
      } else if (d.type === 'test') {
        // Tests as circles
        selection.append('circle')
          .attr('r', d.radius)
          .style('fill', colors.test[d.subType] || '#6c757d')
          .style('stroke', '#fff')
          .style('stroke-width', 2);
          
        // Add result indicator
        const resultIcons = {
          successful: '✓',
          detected: '!',
          blocked: '✗',
          failed: '✗',
          partial: '~',
          unknown: '?'
        };
        
        selection.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', 5)
          .style('font-size', '14px')
          .style('font-weight', 'bold')
          .style('fill', '#fff')
          .text(resultIcons[d.subType] || '?');
      } else if (d.type === 'webtarget') {
        // Web targets as circles with globe icon
        selection.append('circle')
          .attr('r', d.radius)
          .style('fill', '#06b6d4')
          .style('stroke', '#fff')
          .style('stroke-width', 2);
        selection.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', 5)
          .style('font-size', '14px')
          .style('fill', '#fff')
          .text('\u{1F310}');
      } else if (d.type === 'webfinding') {
        // Web findings as small diamonds colored by severity
        const wfColors = { critical: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#22c55e', informational: '#3b82f6' };
        selection.append('rect')
          .attr('x', -d.radius)
          .attr('y', -d.radius)
          .attr('width', d.radius * 2)
          .attr('height', d.radius * 2)
          .attr('transform', 'rotate(45)')
          .style('fill', wfColors[d.severity] || '#06b6d4')
          .style('stroke', '#06b6d4')
          .style('stroke-width', 2);
      }
    });

    // Add labels
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.type === 'tactic' ? 5 : d.radius + 15)
      .style('font-size', d => d.type === 'project' ? '14px' : '12px')
      .style('font-weight', d =>
        d.type === 'project' || d.type === 'tactic' ||
        (d.type === 'target' && d.criticality === 'critical') ? 'bold' : 'normal'
      )
      .style('fill', d => {
        if (d.type === 'project') return colors.project;
        if (d.type === 'tactic') return '#fff';
        return darkMode ? '#e5e7eb' : '#000';
      })
      .text(d => {
        if (d.type === 'project') return d.label;
        if (d.type === 'target') return d.label.substring(0, 20) + (d.label.length > 20 ? '...' : '');
        if (d.type === 'tactic') return d.label;
        if (d.type === 'technique') return d.label;
        if (d.type === 'test') return d.label.substring(0, 15) + (d.label.length > 15 ? '...' : '');
        return '';
      });

    // Add hover tooltip (interactive - user can click "click for details")
    let tooltipHideTimer = null;
    let tooltipNodeData = null;
    let tooltipEnableTimer = null;
    const tooltip = d3.select('body').append('div')
      .attr('class', 'attack-path-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('max-width', '300px')
      .style('box-shadow', '0 4px 6px rgba(0, 0, 0, 0.1)')
      .style('z-index', '9999')
      .style('cursor', 'pointer');

    tooltip
      .on('mouseenter', function() {
        if (tooltipHideTimer) { clearTimeout(tooltipHideTimer); tooltipHideTimer = null; }
      })
      .on('mouseleave', function() {
        tooltip.transition().duration(300).style('opacity', 0).style('pointer-events', 'none');
        if (tooltipEnableTimer) { clearTimeout(tooltipEnableTimer); tooltipEnableTimer = null; }
        tooltipNodeData = null;
      })
      .on('click', function() {
        if (tooltipNodeData) {
          if (tooltipNodeData.type === 'test' && tooltipNodeData.data) {
            setSelectedAction(tooltipNodeData.data);
          } else {
            setSelectedNode(tooltipNodeData);
          }
          tooltip.style('opacity', 0).style('pointer-events', 'none');
          if (tooltipEnableTimer) { clearTimeout(tooltipEnableTimer); tooltipEnableTimer = null; }
          tooltipNodeData = null;
        }
      });

    node
      .on('mouseover', function(event, d) {
        tooltipNodeData = d;
        if (tooltipHideTimer) { clearTimeout(tooltipHideTimer); tooltipHideTimer = null; }

        // Collect IDs of connected nodes
        const connectedNodeIds = new Set();
        connectedNodeIds.add(d.id); // Include the hovered node itself
        links.forEach(l => {
          const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
          const targetId = typeof l.target === 'object' ? l.target.id : l.target;
          if (sourceId === d.id) {
            connectedNodeIds.add(targetId);
          } else if (targetId === d.id) {
            connectedNodeIds.add(sourceId);
          }
        });

        // Highlight connected edges and dim others
        link
          .attr('opacity', l => {
            const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
            const targetId = typeof l.target === 'object' ? l.target.id : l.target;
            return (sourceId === d.id || targetId === d.id) ? 1 : 0.15;
          })
          .attr('stroke-width', l => {
            const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
            const targetId = typeof l.target === 'object' ? l.target.id : l.target;
            if (sourceId === d.id || targetId === d.id) {
              if (l.type === 'has-target') return 4;
              if (l.type === 'enables-tactic') return 3;
              if (l.type === 'contains-technique') return 3;
              if (l.type === 'implemented-by') return 3;
              if (l.type === 'tested-on') return 2.5;
              return 2;
            }
            if (l.type === 'has-target') return 3;
            if (l.type === 'enables-tactic') return 2;
            if (l.type === 'contains-technique') return 2;
            if (l.type === 'implemented-by') return 2;
            if (l.type === 'tested-on') return 1.5;
            return 1;
          })
          .style('filter', l => {
            const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
            const targetId = typeof l.target === 'object' ? l.target.id : l.target;
            return (sourceId === d.id || targetId === d.id) ? 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))' : 'none';
          });

        // Highlight connected nodes and dim others
        node.each(function(n) {
          const nodeGroup = d3.select(this);
          const isConnected = connectedNodeIds.has(n.id);

          nodeGroup.selectAll('circle, rect, polygon')
            .style('filter', isConnected ? 'drop-shadow(0 0 8px rgba(255, 200, 0, 0.9))' : 'none')
            .style('opacity', isConnected ? 1 : 0.3);
        });

        let content = '';
        
        if (d.type === 'project') {
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">${d.label}</div>
            <div>Total Targets: ${nodes.filter(n => n.type === 'target').length}</div>
            <div>Total Tests: ${nodes.filter(n => n.type === 'test').length}</div>
            <div>Total Techniques: ${nodes.filter(n => n.type === 'technique').length}</div>
          `;
        } else if (d.type === 'target') {
          const testsOnTarget = links.filter(l =>
            l.type === 'tested-on' && (typeof l.target === 'object' ? l.target.id : l.target) === d.id
          ).length;
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">${d.label}</div>
            <div>Type: ${d.subType}</div>
            <div>Criticality: <span style="color: ${
              d.criticality === 'critical' ? '#dc3545' :
              d.criticality === 'high' ? '#fd7e14' :
              d.criticality === 'medium' ? '#ffc107' :
              '#28a745'
            }">${d.criticality}</span></div>
            <div>Tests Performed: ${testsOnTarget}</div>
            ${d.description ? `<div style="margin-top: 8px;">${d.description}</div>` : ''}
          `;
        } else if (d.type === 'tactic') {
          const techniqueCount = links.filter(l =>
            l.type === 'contains-technique' && (typeof l.source === 'object' ? l.source.id : l.source) === d.id
          ).length;
          const tacticActions = actions.filter(a => a.mitreAttackTechnique?.tactic === d.label);
          const tacticSuccessful = tacticActions.filter(a => a.result === 'successful').length;
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">MITRE Tactic: ${d.label}</div>
            <div>Techniques: ${techniqueCount}</div>
            <div>Actions: ${tacticActions.length} (${tacticSuccessful} successful)</div>
          `;
        } else if (d.type === 'technique') {
          const testCount = links.filter(l =>
            l.type === 'implemented-by' && (typeof l.source === 'object' ? l.source.id : l.source) === d.id
          ).length;
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">${d.label} - ${d.name}</div>
            <div>Tactic: ${d.tactic}</div>
            <div>Tests Performed: ${testCount}</div>
          `;
        } else if (d.type === 'webtarget') {
          const wtFindings = webFindings.filter(f => f.webTarget === d.data._id || f.webTarget?._id === d.data._id);
          content = `
            <div style="font-weight: bold; margin-bottom: 8px; color: #06b6d4;">${d.label}</div>
            <div>Type: Web Target</div>
            <div>Findings: ${wtFindings.length}</div>
          `;
        } else if (d.type === 'webfinding') {
          const sevColor = { critical: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#22c55e' };
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">${d.data.title}</div>
            <div>Severity: <span style="color: ${sevColor[d.severity] || '#06b6d4'}">${d.severity}</span></div>
            ${d.data.wstgTest?.testId ? `<div>WSTG: ${d.data.wstgTest.testId}</div>` : ''}
            ${d.data.endpoint?.url ? `<div>URL: ${d.data.endpoint.url}</div>` : ''}
          `;
        } else if (d.type === 'test') {
          const testTargets = d.data.targets || [];
          const testSources = d.data.sources || [];
          let targetsHtml = '';
          if (testTargets.length > 0) {
            targetsHtml += '<div style="margin-top:6px;border-top:1px solid #374151;padding-top:4px">';
            testTargets.slice(0, 4).forEach(tgt => {
              const val = typeof tgt === 'object' ? (tgt.value || tgt.name || tgt._id) : tgt;
              targetsHtml += `<div style="color:#fb923c">\u2193 ${val}</div>`;
            });
            if (testTargets.length > 4) targetsHtml += `<div style="color:#fb923c">+${testTargets.length - 4} more</div>`;
            targetsHtml += '</div>';
          }
          if (testSources.length > 0) {
            targetsHtml += `<div${testTargets.length === 0 ? ' style="margin-top:6px;border-top:1px solid #374151;padding-top:4px"' : ''}>`;
            testSources.slice(0, 3).forEach(src => {
              const val = typeof src === 'object' ? (src.value || src.name || src._id) : src;
              targetsHtml += `<div style="color:#2dd4bf">\u2191 ${val}</div>`;
            });
            if (testSources.length > 3) targetsHtml += `<div style="color:#2dd4bf">+${testSources.length - 3} more</div>`;
            targetsHtml += '</div>';
          }
          content = `
            <div style="font-weight: bold; margin-bottom: 8px;">${d.label}</div>
            <div>Result: <span style="color: ${colors.test[d.subType]}">${d.subType}</span></div>
            ${d.technique ? `<div>Technique: ${d.technique.id} - ${d.technique.name}</div>` : ''}
            <div>Time: ${new Date(d.timestamp).toLocaleString()}</div>
            ${d.data.description ? `<div style="margin-top: 8px;">${d.data.description.substring(0, 150)}${d.data.description.length > 150 ? '...' : ''}</div>` : ''}
            ${targetsHtml}
          `;
        }
        
        tooltip.transition().duration(200).style('opacity', .9);
        tooltip.html(content)
          .style('left', (event.pageX + 15) + 'px')
          .style('top', (event.pageY + 15) + 'px');

        // Enable pointer events after tooltip is positioned (prevents interference with hover detection)
        if (tooltipEnableTimer) clearTimeout(tooltipEnableTimer);
        tooltipEnableTimer = setTimeout(() => {
          tooltip.style('pointer-events', 'auto');
        }, 250);
      })
      .on('mouseout', function() {
        // Reset edge highlighting
        link
          .attr('opacity', d => {
            if (d.type === 'sequence') return 0.3;
            if (d.type === 'tested-on') return 0.5;
            return 0.7;
          })
          .attr('stroke-width', d => {
            if (d.type === 'has-target') return 3;
            if (d.type === 'enables-tactic') return 2;
            if (d.type === 'contains-technique') return 2;
            if (d.type === 'implemented-by') return 2;
            if (d.type === 'tested-on') return 1.5;
            if (d.type === 'sequence') return 1;
            return 1;
          })
          .style('filter', 'none');

        // Reset node highlighting
        node.each(function() {
          d3.select(this).selectAll('circle, rect, polygon')
            .style('filter', 'none')
            .style('opacity', 1);
        });

        tooltipHideTimer = setTimeout(() => {
          tooltip.transition().duration(300).style('opacity', 0).style('pointer-events', 'none');
          if (tooltipEnableTimer) { clearTimeout(tooltipEnableTimer); tooltipEnableTimer = null; }
          tooltipNodeData = null;
        }, 500);
      })
      .on('click', function(event, d) {
        if (d.type === 'test' && d.data) {
          setSelectedAction(d.data);
        } else {
          setSelectedNode(d);
        }
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      if (d.type !== 'project') { // Keep project fixed
        d.fx = null;
        d.fy = null;
      }
    }

    // Add legend
    const legend = svg.append('g')
      .attr('transform', `translate(20, 20)`);

    const legendItems = [
      { type: 'project', label: 'Project', shape: 'rounded-rect', color: colors.project },
      { type: 'target', label: 'Target', shape: 'hexagon', color: '#0066cc' },
      { type: 'target-critical', label: 'Critical Target', shape: 'hexagon', color: '#dc3545' },
      { type: 'tactic', label: 'MITRE Tactic', shape: 'rounded-rect', color: colors.tactic },
      { type: 'technique', label: 'MITRE Technique', shape: 'diamond', color: colors.technique },
      { type: 'test-success', label: 'Successful Test', shape: 'circle', color: '#28a745' },
      { type: 'test-detected', label: 'Detected Test', shape: 'circle', color: '#ffc107' },
      { type: 'test-blocked', label: 'Blocked Test', shape: 'circle', color: '#dc3545' },
      ...(webTargets.length > 0 ? [
        { type: 'webtarget', label: 'Web Target', shape: 'circle', color: '#06b6d4' },
        { type: 'webfinding', label: 'Web Finding', shape: 'diamond', color: '#06b6d4' }
      ] : [])
    ];

    legendItems.forEach((item, i) => {
      const legendItem = legend.append('g')
        .attr('transform', `translate(0, ${i * 25})`);

      if (item.shape === 'rounded-rect') {
        legendItem.append('rect')
          .attr('x', 0)
          .attr('y', -8)
          .attr('width', 20)
          .attr('height', 16)
          .attr('rx', 4)
          .style('fill', item.color)
          .style('stroke', '#fff')
          .style('stroke-width', 1);
      } else if (item.shape === 'hexagon') {
        const hexagon = (r) => {
          const angles = d3.range(0, 2 * Math.PI, Math.PI / 3);
          return angles.map(a => [r * Math.cos(a), r * Math.sin(a)]).join(' ');
        };
        legendItem.append('polygon')
          .attr('points', hexagon(8))
          .attr('transform', 'translate(10, 0)')
          .style('fill', item.color)
          .style('stroke', '#fff')
          .style('stroke-width', 1);
      } else if (item.shape === 'circle') {
        legendItem.append('circle')
          .attr('cx', 10)
          .attr('cy', 0)
          .attr('r', 8)
          .style('fill', item.color)
          .style('stroke', '#fff')
          .style('stroke-width', 1);
      } else if (item.shape === 'diamond') {
        legendItem.append('rect')
          .attr('x', 2)
          .attr('y', -8)
          .attr('width', 16)
          .attr('height', 16)
          .attr('transform', 'rotate(45, 10, 0)')
          .style('fill', item.color)
          .style('stroke', '#fff')
          .style('stroke-width', 1);
      }

      legendItem.append('text')
        .attr('x', 25)
        .attr('y', 4)
        .style('font-size', '12px')
        .style('fill', darkMode ? '#e5e7eb' : '#000')
        .text(item.label);
    });

    // Clean up on unmount
    return () => {
      tooltip.remove();
    };
  };

  const drawTimelineView = () => {
    const svg = d3.select(svgRef.current);
    const { height } = dimensions;

    // Sort actions by timestamp
    const sortedActions = [...actions].sort((a, b) =>
      new Date(a.startTimestamp || a.timestamp) - new Date(b.startTimestamp || b.timestamp)
    );

    // Calculate dynamic width to prevent text overlap (min 80px per action)
    const minWidth = Math.max(dimensions.width, sortedActions.length * 80 + 200);
    svg.attr('width', minWidth);
    const width = minWidth;

    // Add background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', darkMode ? '#1f2937' : 'white');

    const textColor = darkMode ? '#e5e7eb' : '#000';

    // Create time scale
    const timeExtent = d3.extent(sortedActions, d => new Date(d.startTimestamp || d.timestamp));
    const timeScale = d3.scaleTime()
      .domain(timeExtent)
      .range([100, width - 50]);

    // Group actions by result type
    const resultTypes = ['successful', 'detected', 'blocked', 'failed', 'partial', 'unknown'];
    const yScale = d3.scaleBand()
      .domain(resultTypes)
      .range([50, height - 100])
      .padding(0.2);

    // Add time axis
    const timeAxis = d3.axisBottom(timeScale)
      .tickFormat(d3.timeFormat('%m/%d %H:%M'));
    
    const axisG = svg.append('g')
      .attr('transform', `translate(0, ${height - 80})`)
      .call(timeAxis);
    axisG.selectAll('text')
      .style('text-anchor', 'end')
      .style('fill', textColor)
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');
    axisG.selectAll('line').style('stroke', darkMode ? '#4b5563' : '#000');
    axisG.selectAll('path').style('stroke', darkMode ? '#4b5563' : '#000');

    // Add result type labels
    svg.selectAll('.result-label')
      .data(resultTypes)
      .enter().append('text')
      .attr('class', 'result-label')
      .attr('x', 90)
      .attr('y', d => yScale(d) + yScale.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dy', '.35em')
      .style('font-size', '12px')
      .style('fill', textColor)
      .style('text-transform', 'capitalize')
      .text(d => d);

    // Add actions
    const actionGroups = svg.selectAll('.action-group')
      .data(sortedActions)
      .enter().append('g')
      .attr('class', 'action-group')
      .attr('transform', d => `translate(${timeScale(new Date(d.startTimestamp || d.timestamp))}, ${yScale(d.result || 'unknown')})`);

    actionGroups.append('circle')
      .attr('r', 10)
      .attr('cy', yScale.bandwidth() / 2)
      .style('fill', d => {
        const colors = {
          'successful': '#28a745',
          'detected': '#ffc107',
          'blocked': '#dc3545',
          'failed': '#6c757d',
          'partial': '#17a2b8',
          'unknown': '#6610f2'
        };
        return colors[d.result] || '#6610f2';
      })
      .style('stroke', '#fff')
      .style('stroke-width', 2);

    // Add technique labels
    actionGroups.append('text')
      .attr('y', yScale.bandwidth() / 2 - 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', textColor)
      .text(d => d.mitreAttackTechnique ? d.mitreAttackTechnique.id : '');

    // Add action titles on hover with improved tooltip
    let tlTooltipTimer = null;
    let tlTooltipAction = null;
    const timelineTooltip = d3.select('body').append('div')
      .attr('class', 'timeline-tooltip-div')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '12px')
      .style('pointer-events', 'auto')
      .style('max-width', '300px')
      .style('box-shadow', '0 4px 6px rgba(0, 0, 0, 0.3)')
      .style('z-index', '9999')
      .style('cursor', 'pointer');

    timelineTooltip
      .on('mouseenter', function() {
        if (tlTooltipTimer) { clearTimeout(tlTooltipTimer); tlTooltipTimer = null; }
      })
      .on('mouseleave', function() {
        timelineTooltip.transition().duration(300).style('opacity', 0);
        tlTooltipAction = null;
      })
      .on('click', function() {
        if (tlTooltipAction) {
          setSelectedAction(tlTooltipAction);
          timelineTooltip.style('opacity', 0);
          tlTooltipAction = null;
        }
      });

    actionGroups
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        tlTooltipAction = d;
        if (tlTooltipTimer) { clearTimeout(tlTooltipTimer); tlTooltipTimer = null; }
        let content = `<div style="font-weight:bold;margin-bottom:6px">${d.title}</div>`;
        if (d.mitreAttackTechnique) {
          content += `<div>${d.mitreAttackTechnique.id} - ${d.mitreAttackTechnique.name}</div>`;
          content += `<div>Tactic: ${d.mitreAttackTechnique.tactic}</div>`;
        }
        content += `<div>Result: ${d.result || 'unknown'}</div>`;
        // Add targets/sources
        const tlTargets = d.targets || [];
        const tlSources = d.sources || [];
        if (tlTargets.length > 0) {
          content += '<div style="margin-top:6px;border-top:1px solid #374151;padding-top:4px">';
          tlTargets.slice(0, 4).forEach(tgt => {
            const val = typeof tgt === 'object' ? (tgt.value || tgt.name || tgt._id) : tgt;
            content += `<div style="color:#fb923c">\u2193 ${val}</div>`;
          });
          if (tlTargets.length > 4) content += `<div style="color:#fb923c">+${tlTargets.length - 4} more</div>`;
          content += '</div>';
        }
        if (tlSources.length > 0) {
          content += `<div${tlTargets.length === 0 ? ' style="margin-top:6px;border-top:1px solid #374151;padding-top:4px"' : ''}>`;
          tlSources.slice(0, 3).forEach(src => {
            const val = typeof src === 'object' ? (src.value || src.name || src._id) : src;
            content += `<div style="color:#2dd4bf">\u2191 ${val}</div>`;
          });
          if (tlSources.length > 3) content += `<div style="color:#2dd4bf">+${tlSources.length - 3} more</div>`;
          content += '</div>';
        }
        timelineTooltip.transition().duration(200).style('opacity', .95);
        timelineTooltip.html(content)
          .style('left', (event.pageX - 5) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseout', function() {
        tlTooltipTimer = setTimeout(() => {
          timelineTooltip.transition().duration(300).style('opacity', 0);
          tlTooltipAction = null;
        }, 500);
      })
      .on('click', function(event, d) {
        setSelectedAction(d);
      });

    // Add kill chain phases as background
    const killChainPhases = [
      { name: 'Initial Access', start: 0, color: '#e6f2ff' },
      { name: 'Execution', start: 0.2, color: '#cce5ff' },
      { name: 'Persistence', start: 0.4, color: '#b3d9ff' },
      { name: 'Privilege Escalation', start: 0.6, color: '#99ccff' },
      { name: 'Defense Evasion', start: 0.8, color: '#80bfff' }
    ];

    const phaseScale = d3.scaleLinear()
      .domain([0, 1])
      .range([100, width - 50]);

    killChainPhases.forEach((phase, i) => {
      const nextStart = killChainPhases[i + 1]?.start || 1;
      svg.append('rect')
        .attr('x', phaseScale(phase.start))
        .attr('y', 30)
        .attr('width', phaseScale(nextStart) - phaseScale(phase.start))
        .attr('height', height - 110)
        .style('fill', phase.color)
        .style('opacity', 0.3);
      
      svg.append('text')
        .attr('x', (phaseScale(phase.start) + phaseScale(nextStart)) / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', textColor)
        .text(phase.name);
    });

    // Clean up tooltip on unmount
    return () => {
      timelineTooltip.remove();
    };
  };

  const drawKillChainView = () => {
    const svg = d3.select(svgRef.current);
    const { width, height } = dimensions;

    // Add background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', darkMode ? '#1f2937' : 'white');

    const kcTextColor = darkMode ? '#e5e7eb' : '#000';

    // Group actions by MITRE tactic
    const tacticGroups = {};
    actions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        const tactic = action.mitreAttackTechnique.tactic;
        if (!tacticGroups[tactic]) {
          tacticGroups[tactic] = [];
        }
        tacticGroups[tactic].push(action);
      }
    });

    const killChainOrder = [
      'Reconnaissance',
      'Resource Development',
      'Initial Access',
      'Execution',
      'Persistence',
      'Privilege Escalation',
      'Defense Evasion',
      'Credential Access',
      'Discovery',
      'Lateral Movement',
      'Collection',
      'Command and Control',
      'Exfiltration',
      'Impact'
    ];

    // Filter to only show tactics with actions
    const activeTactics = killChainOrder.filter(tactic => tacticGroups[tactic]);

    // Create tooltip for kill chain (interactive)
    let kcTooltipTimer = null;
    let kcTooltipData = null;
    const kcTooltip = d3.select('body').append('div')
      .attr('class', 'kc-tooltip-div')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '12px')
      .style('pointer-events', 'auto')
      .style('max-width', '350px')
      .style('box-shadow', '0 4px 6px rgba(0, 0, 0, 0.3)')
      .style('z-index', '9999')
      .style('cursor', 'pointer');

    kcTooltip
      .on('mouseenter', function() {
        if (kcTooltipTimer) { clearTimeout(kcTooltipTimer); kcTooltipTimer = null; }
      })
      .on('mouseleave', function() {
        kcTooltip.transition().duration(300).style('opacity', 0);
        kcTooltipData = null;
      })
      .on('click', function() {
        if (kcTooltipData) {
          if (kcTooltipData.actions.length === 1) {
            setSelectedAction(kcTooltipData.actions[0]);
          } else {
            setSelectedNode({
              type: 'technique',
              data: kcTooltipData.technique,
              label: kcTooltipData.technique.id,
              name: kcTooltipData.technique.name,
              tactic: kcTooltipData.tactic
            });
          }
          kcTooltip.style('opacity', 0);
          kcTooltipData = null;
        }
      });

    // Create scales
    const xScale = d3.scaleBand()
      .domain(activeTactics)
      .range([50, width - 50])
      .padding(0.1);

    // Add tactic columns
    activeTactics.forEach((tactic, i) => {
      const tacticG = svg.append('g')
        .attr('transform', `translate(${xScale(tactic)}, 0)`);

      // Add background column
      tacticG.append('rect')
        .attr('x', 0)
        .attr('y', 60)
        .attr('width', xScale.bandwidth())
        .attr('height', height - 80)
        .style('fill', darkMode ? (i % 2 === 0 ? '#1f2937' : '#111827') : (i % 2 === 0 ? '#f8f9fa' : '#ffffff'))
        .style('stroke', darkMode ? '#374151' : '#e5e7eb')
        .style('stroke-width', 1);

      // Add tactic label
      tacticG.append('text')
        .attr('x', xScale.bandwidth() / 2)
        .attr('y', 40)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', kcTextColor)
        .text(tactic);

      // Add actions in this tactic
      const tacticsActions = tacticGroups[tactic];
      const techniqueGroups = {};
      
      // Group by technique
      tacticsActions.forEach(action => {
        const techniqueId = action.mitreAttackTechnique.id;
        if (!techniqueGroups[techniqueId]) {
          techniqueGroups[techniqueId] = {
            technique: action.mitreAttackTechnique,
            actions: []
          };
        }
        techniqueGroups[techniqueId].actions.push(action);
      });

      let yOffset = 80;
      Object.values(techniqueGroups).forEach(group => {
        const techniqueG = tacticG.append('g')
          .attr('transform', `translate(${xScale.bandwidth() / 2}, ${yOffset})`)
          .style('cursor', 'pointer');

        // Calculate success rate
        const successCount = group.actions.filter(a => a.result === 'successful').length;
        const successRate = (successCount / group.actions.length) * 100;

        // Draw technique box
        techniqueG.append('rect')
          .attr('x', -35)
          .attr('y', -15)
          .attr('width', 70)
          .attr('height', 30)
          .attr('rx', 5)
          .style('fill', successRate >= 70 ? '#28a745' :
                        successRate >= 30 ? '#ffc107' : '#dc3545')
          .style('opacity', 0.8);

        techniqueG.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '.35em')
          .style('fill', 'white')
          .style('font-size', '11px')
          .style('font-weight', 'bold')
          .text(group.technique.id);

        // Add technique name below
        techniqueG.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', 25)
          .style('fill', kcTextColor)
          .style('font-size', '9px')
          .text(group.technique.name?.substring(0, 16) + (group.technique.name?.length > 16 ? '...' : ''));

        // Add count indicator
        techniqueG.append('circle')
          .attr('cx', 30)
          .attr('cy', -10)
          .attr('r', 10)
          .style('fill', '#003d82')
          .style('stroke', '#fff')
          .style('stroke-width', 2);

        techniqueG.append('text')
          .attr('x', 30)
          .attr('y', -10)
          .attr('text-anchor', 'middle')
          .attr('dy', '.35em')
          .style('fill', 'white')
          .style('font-size', '10px')
          .style('font-weight', 'bold')
          .text(group.actions.length);

        // Add hover and click handlers
        techniqueG
          .on('mouseover', function(event) {
            kcTooltipData = { actions: group.actions, technique: group.technique, tactic };
            if (kcTooltipTimer) { clearTimeout(kcTooltipTimer); kcTooltipTimer = null; }
            d3.select(this).select('rect').style('opacity', 1).style('stroke', '#fff').style('stroke-width', 2);
            kcTooltip.transition().duration(200).style('opacity', .95);
            let content = `<div style="font-weight:bold;margin-bottom:6px">${group.technique.id} - ${group.technique.name}</div>`;
            content += `<div>Actions: ${group.actions.length}</div>`;
            content += `<div>Success Rate: ${Math.round(successRate)}%</div>`;
            content += `<div style="margin-top:4px;font-size:11px">`;
            group.actions.forEach(a => {
              const color = a.result === 'successful' ? '#4ade80' : a.result === 'detected' ? '#fbbf24' : a.result === 'blocked' ? '#f87171' : '#9ca3af';
              const aTargets = (a.targets || []).slice(0, 3).map(tgt => typeof tgt === 'object' ? (tgt.value || tgt.name || tgt._id) : tgt);
              const targetStr = aTargets.length > 0 ? ` <span style="color:#fb923c">[${aTargets.join(', ')}${(a.targets || []).length > 3 ? ', ...' : ''}]</span>` : '';
              content += `<div style="color:${color}">- ${a.title?.substring(0, 30) || 'Unnamed'}${targetStr}</div>`;
            });
            content += `</div>`;
                kcTooltip.html(content)
              .style('left', (event.pageX - 5) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseout', function() {
            d3.select(this).select('rect').style('opacity', 0.8).style('stroke', 'none');
            kcTooltipTimer = setTimeout(() => {
              kcTooltip.transition().duration(300).style('opacity', 0);
              kcTooltipData = null;
            }, 500);
          })
          .on('click', function() {
            if (group.actions.length === 1) {
              setSelectedAction(group.actions[0]);
            } else {
              // Show first action detail, or show technique info
              setSelectedNode({
                type: 'technique',
                data: group.technique,
                label: group.technique.id,
                name: group.technique.name,
                tactic: tactic
              });
            }
          });

        yOffset += 50;
      });
    });

    // Add summary statistics
    const statsG = svg.append('g')
      .attr('transform', `translate(${width - 200}, 20)`);

    statsG.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 180)
      .attr('height', 100)
      .attr('rx', 5)
      .style('fill', darkMode ? '#374151' : '#f8f9fa')
      .style('stroke', darkMode ? '#4b5563' : '#dee2e6');

    statsG.append('text')
      .attr('x', 90)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .style('font-weight', 'bold')
      .style('fill', kcTextColor)
      .text('Attack Summary');

    const stats = [
      { label: 'Total Actions:', value: actions.length },
      { label: 'Tactics Used:', value: activeTactics.length },
      { label: 'Success Rate:', value: Math.round((actions.filter(a => a.result === 'successful').length / actions.length) * 100) + '%' }
    ];

    stats.forEach((stat, i) => {
      statsG.append('text')
        .attr('x', 10)
        .attr('y', 45 + i * 20)
        .style('font-size', '12px')
        .style('fill', kcTextColor)
        .text(stat.label);

      statsG.append('text')
        .attr('x', 170)
        .attr('y', 45 + i * 20)
        .attr('text-anchor', 'end')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', kcTextColor)
        .text(stat.value);
    });

    // Clean up tooltip on unmount
    return () => {
      kcTooltip.remove();
    };
  };

  return (
    <div className="attack-path-container">
      {/* Controls */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-4 py-2 rounded-md flex items-center ${
                viewMode === 'timeline'
                  ? 'bg-primary text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faCalendarDays} className="mr-2" />
              {t('attackChain.timelineView') || 'Timeline View'}
            </button>
            <button
              onClick={() => setViewMode('network')}
              className={`px-4 py-2 rounded-md flex items-center ${
                viewMode === 'network'
                  ? 'bg-primary text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faNetworkWired} className="mr-2" />
              {t('attackChain.attackFlow') || 'Attack Flow'}
            </button>
            <button
              onClick={() => setViewMode('killchain')}
              className={`px-4 py-2 rounded-md flex items-center ${
                viewMode === 'killchain'
                  ? 'bg-primary text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FontAwesomeIcon icon={faLink} className="mr-2" />
              {t('attackChain.killChainView') || 'Kill Chain'}
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            {viewMode === 'network' && (
              <>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filterOptions.showSuccessfulOnly}
                    onChange={(e) => setFilterOptions({...filterOptions, showSuccessfulOnly: e.target.checked})}
                    className="mr-2 text-primary"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : ''}`}>Successful Only</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filterOptions.showCriticalOnly}
                    onChange={(e) => setFilterOptions({...filterOptions, showCriticalOnly: e.target.checked})}
                    className="mr-2 text-primary"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : ''}`}>Critical Targets</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filterOptions.showTechniquesOnly}
                    onChange={(e) => setFilterOptions({...filterOptions, showTechniquesOnly: e.target.checked})}
                    className="mr-2 text-primary"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : ''}`}>MITRE Focus</span>
                </label>
              </>
            )}
            <button
              onClick={exportToPNG}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark flex items-center"
            >
              <FontAwesomeIcon icon={faCamera} className="mr-2" />
              Export PNG
            </button>
          </div>
        </div>
        
        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {viewMode === 'timeline' && 'Chronological view of actions grouped by result type'}
          {viewMode === 'network' && 'Attack flow visualization: Tactics → Techniques → Actions → Targets'}
          {viewMode === 'killchain' && 'Actions mapped to cyber kill chain phases with success metrics'}
        </div>
      </div>

      {/* Visualization */}
      {viewMode === 'timeline' ? (
        <div ref={containerRef}>
          <AttackChainVisualization
            actions={actions}
            height={600}
            projectId={projectId}
            initialViewMode="timeline"
            hideControls
          />
        </div>
      ) : (
        <div ref={containerRef} className={`rounded-lg shadow-md p-4 overflow-x-auto border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <svg ref={svgRef} width={dimensions.width} height={dimensions.height}></svg>
        </div>
      )}

      {/* Selected Node Details */}
      {selectedNode && (
        <div className={`mt-4 rounded-lg p-4 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex justify-between items-start mb-2">
            <h3 className={`font-semibold text-lg flex items-center ${darkMode ? 'text-white' : ''}`}>
              {selectedNode.type === 'tactic' && <><FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-primary" /> Tactic Details</>}
              {selectedNode.type === 'target' && <><FontAwesomeIcon icon={faBullseye} className="mr-2 text-primary" /> Target Details</>}
              {selectedNode.type === 'action' && <><FontAwesomeIcon icon={faBolt} className="mr-2 text-primary" /> Action Details</>}
              {selectedNode.type === 'technique' && <><FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-primary" /> Technique Details</>}
              {!['tactic', 'target', 'action', 'technique'].includes(selectedNode.type) && 'Node Details'}
            </h3>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="space-y-2 text-sm">
            {selectedNode.type === 'tactic' && (
              <>
                <div><strong>Tactic:</strong> {selectedNode.label}</div>
                <div><strong>Techniques in this tactic:</strong> {
                  actions.filter(a => a.mitreAttackTechnique?.tactic === selectedNode.label)
                    .map(a => a.mitreAttackTechnique.id)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .join(', ')
                }</div>
              </>
            )}
            
            {selectedNode.type === 'target' && (
              <>
                <div><strong>Value:</strong> {selectedNode.data.value}</div>
                <div><strong>Type:</strong> {selectedNode.data.type}</div>
                {selectedNode.data.description && (
                  <div><strong>Description:</strong> {selectedNode.data.description}</div>
                )}
                {selectedNode.data.metadata?.criticality && (
                  <div><strong>Criticality:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      selectedNode.data.metadata.criticality === 'critical' ? 'bg-red-100 text-red-800' :
                      selectedNode.data.metadata.criticality === 'high' ? 'bg-orange-100 text-orange-800' :
                      selectedNode.data.metadata.criticality === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {selectedNode.data.metadata.criticality}
                    </span>
                  </div>
                )}
              </>
            )}
            
            {selectedNode.type === 'action' && (
              <>
                <div><strong>Title:</strong> {selectedNode.data.title}</div>
                <div><strong>Description:</strong> {selectedNode.data.description}</div>
                <div><strong>Result:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    selectedNode.data.result === 'successful' ? 'bg-green-100 text-green-800' :
                    selectedNode.data.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                    selectedNode.data.result === 'blocked' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedNode.data.result}
                  </span>
                </div>
                <div><strong>Timestamp:</strong> {new Date(selectedNode.data.startTimestamp || selectedNode.data.timestamp).toLocaleString()}</div>
                {selectedNode.data.mitreAttackTechnique && (
                  <div><strong>MITRE Technique:</strong> {selectedNode.data.mitreAttackTechnique.id} - {selectedNode.data.mitreAttackTechnique.name}</div>
                )}
                {selectedNode.data.detectionMethods && selectedNode.data.detectionMethods.length > 0 && (
                  <div><strong>Detection Methods:</strong> {selectedNode.data.detectionMethods.join(', ')}</div>
                )}
              </>
            )}
            
            {selectedNode.type === 'technique' && (
              <>
                <div><strong>ID:</strong> {selectedNode.data.id}</div>
                <div><strong>Name:</strong> {selectedNode.data.name}</div>
                <div><strong>Tactic:</strong> {selectedNode.data.tactic}</div>
                <div><strong>Times Used:</strong> {
                  actions.filter(a => a.mitreAttackTechnique?.id === selectedNode.data.id).length
                }</div>
                <div><strong>Success Rate:</strong> {
                  (() => {
                    const techniqueActions = actions.filter(a => a.mitreAttackTechnique?.id === selectedNode.data.id);
                    const successful = techniqueActions.filter(a => a.result === 'successful').length;
                    return techniqueActions.length > 0 ? 
                      `${Math.round((successful / techniqueActions.length) * 100)}%` : 'N/A';
                  })()
                }</div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
    </div>
  );
};

export default AttackPathVisualization;